import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "../App";

const UserSheetContext = createContext(null);

// ── Provider ──────────────────────────────────────────────────────────────────

export const UserSheetProvider = ({ children }) => {
  const { user } = useAuth();
  const [openUserId, setOpenUserId] = useState(null);
  const [allUsers, setAllUsers] = useState([]);

  const fetchUsers = useCallback(async () => {
    if (!user || !["admin", "hr", "manager"].includes(user.role)) return;
    try {
      const res = await axios.get(`${API}/users`);
      setAllUsers(res.data);
    } catch {}
  }, [user?.id, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const accessibleIds = useMemo(
    () => new Set(allUsers.map(u => u.id)),
    [allUsers]
  );

  const canViewUser = useCallback((userId) => {
    if (!user || !userId) return false;
    if (["admin", "hr"].includes(user.role)) return true;
    if (user.role === "manager") return accessibleIds.has(userId);
    return false;
  }, [user, accessibleIds]);

  const openSheet = useCallback((userId) => {
    if (canViewUser(userId)) setOpenUserId(userId);
  }, [canViewUser]);

  const closeSheet = useCallback(() => setOpenUserId(null), []);

  return (
    <UserSheetContext.Provider value={{ openSheet, closeSheet, openUserId, allUsers, fetchUsers, canViewUser }}>
      {children}
    </UserSheetContext.Provider>
  );
};

export const useUserSheet = () => useContext(UserSheetContext);

// ── ClickableUser ─────────────────────────────────────────────────────────────
// Wrapper : rend le nom cliquable si l'utilisateur courant a le droit de voir
// la fiche du collaborateur ciblé. Sinon, affiche un simple <span>.

export const ClickableUser = ({ userId, children, className = "" }) => {
  const ctx = useContext(UserSheetContext);
  if (!ctx || !userId || !ctx.canViewUser(userId)) {
    return <span className={className}>{children}</span>;
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); ctx.openSheet(userId); }}
      className={`text-left hover:underline hover:text-[#1A4D2E] dark:hover:text-[#4B8BF4] transition-colors cursor-pointer ${className}`}
    >
      {children}
    </button>
  );
};
